import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X,
  Plus, GitBranch, CheckCircle2, XCircle, Star, RefreshCcw,
  Pencil, Trash2, Power, PowerOff, AlertCircle,
} from 'lucide-react';

import { workflowApi }    from '@/api/workflow.api';
import { usePermission }  from '@/hooks/usePermission';
import { Permissions }    from '@/constants/permissions';
import { AppRoutes }      from '@/constants/routes';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { Spinner }        from '@/components/ui/Spinner';
import type { WorkflowListDto } from '@/types/workflow.types';

export const WorkflowListPage = () => {
  const navigate         = useNavigate();
  const { hasPermission } = usePermission();

  const canView    = hasPermission(Permissions.CFG_WORKFLOW_VIEW);
  const canCreate  = hasPermission(Permissions.CFG_WORKFLOW_CREATE);
  const canEdit    = hasPermission(Permissions.CFG_WORKFLOW_EDIT);
  const canDelete  = hasPermission(Permissions.CFG_WORKFLOW_DELETE);

  const [items,   setItems]   = useState<WorkflowListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [busy,    setBusy]    = useState<string | null>(null); // id of item being actioned

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await workflowApi.getList({ pageSize: 100 });
      setItems(res.data.data?.items ?? []);
    } catch {
      setError('Failed to load workflow definitions.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete workflow "${name}"? This cannot be undone.`)) return;
    setBusy(id);
    try {
      await workflowApi.delete(id);
      setItems(prev => prev.filter(w => w.id !== id));
    } catch {
      setError('Delete failed.');
    } finally {
      setBusy(null);
    }
  };

  const handleActivate = async (id: string) => {
    setBusy(id);
    try {
      await workflowApi.activate(id, false);
      await load();
    } catch {
      setError('Activate failed.');
    } finally {
      setBusy(null);
    }
  };

  const handleDeactivate = async (id: string) => {
    setBusy(id);
    try {
      await workflowApi.deactivate(id);
      await load();
    } catch {
      setError('Deactivate failed.');
    } finally {
      setBusy(null);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p>You do not have permission to view workflow definitions.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      <PageHeader
        icon={GitBranch}
        title="Workflow Definitions"
        subtitle="Approval routes, and which transactions each one governs"
        actions={
          <div className="flex items-center gap-2">
            <IconButton label="Refresh" icon={<RefreshCcw size={15} />} onClick={load} />
            {canCreate && (
              <Button size="sm" leftIcon={<Plus size={15} />}
                onClick={() => navigate(AppRoutes.CFG_WORKFLOW_NEW)}>
                New Workflow
              </Button>
            )}
          </div>
        }
      />

      {/* ── Content ──────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">

        {error && (
          <div className="flex items-center gap-2 mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
            <AlertCircle size={14} className="shrink-0" />
            <span>{error}</span>
            <IconButton label="Dismiss" variant="bare" size="xs" className="ml-auto"
              icon={<X size={14} />} onClick={() => setError(null)} />
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-slate-400">
            <GitBranch size={48} className="mb-3 opacity-20" />
            <p className="text-base font-medium mb-1">No workflow definitions yet</p>
            {canCreate && (
              <Button className="mt-4" leftIcon={<Plus size={14} />}
                onClick={() => navigate(AppRoutes.CFG_WORKFLOW_NEW)}>
                Create your first workflow
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Tx Type</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Levels</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Default</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wide">Effective</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {items.map(w => (
                  <tr key={w.id} className="group hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700 dark:text-blue-400">
                      {w.code}
                    </td>
                    <td className="px-4 py-3">
                      {/* A link, not a button: this navigates, so it should open in a new tab on
                          a middle click or a ctrl-click like any other link in the application. */}
                      <Link
                        to={AppRoutes.CFG_WORKFLOW_DETAIL.replace(':id', w.id)}
                        className="font-medium text-slate-800 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        {w.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-700
                                       text-[10px] font-semibold text-slate-600 dark:text-slate-300 font-mono">
                        {w.transactionTypeCode}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600 dark:text-slate-300">
                      {w.levelCount}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {w.isActive ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                         bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-semibold">
                          <CheckCircle2 size={10} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                         bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400 text-[10px] font-semibold">
                          <XCircle size={10} /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {w.isDefault && (
                        <Star size={14} className="text-amber-500 mx-auto" fill="currentColor" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">
                      {w.effectiveFrom ? new Date(w.effectiveFrom).toLocaleDateString() : '—'}
                      {w.effectiveTo ? ` → ${new Date(w.effectiveTo).toLocaleDateString()}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {busy === w.id ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            {canEdit && (
                              <IconButton
                                label="Edit"
                                variant="bare"
                                size="xs"
                                icon={<Pencil size={13} />}
                                onClick={() => navigate(AppRoutes.CFG_WORKFLOW_DETAIL.replace(':id', w.id))}
                                className="hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                              />
                            )}
                            {canEdit && (w.isActive ? (
                              <IconButton
                                label="Deactivate"
                                variant="bare"
                                size="xs"
                                icon={<PowerOff size={13} />}
                                onClick={() => handleDeactivate(w.id)}
                                className="hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                              />
                            ) : (
                              <IconButton
                                label="Activate"
                                variant="bare"
                                size="xs"
                                icon={<Power size={13} />}
                                onClick={() => handleActivate(w.id)}
                                className="hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                              />
                            ))}
                            {canDelete && (
                              <IconButton
                                label="Delete"
                                variant="bare"
                                size="xs"
                                icon={<Trash2 size={13} />}
                                onClick={() => handleDelete(w.id, w.name)}
                                className="hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                              />
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
